import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  actOnStaysListing,
  getStaysListing,
  getUnitStays,
  listBuildingStaysUnits,
  listListingReservations,
  listStaysBuildings,
} from '../../../../api/stays-client';
import { getGuide } from '../../../../api/stays-guide-client';
import { getReadiness, ListingReadiness, saveSetupProgress, SetupStepKey } from '../../../../api/stays-setup-client';
import type { GuideBundle } from '../../../../interfaces/stays-guide';
import type { StaysBuilding, StaysBuildingUnits, StaysListing, StayReservation } from '../../../../interfaces/stays';
import { useAuth } from '../../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import { StateSwitch, StepTransition } from '../../../../motion';
import { AppButton } from '../../../button/app-button';
import { StatusPill } from '../../../status-pill/status-pill';
import { VisuallyHidden } from '../../../../style-utils/text';
import { FormSkeleton, LoadError } from '../../dashboard/dashboard-shared-components/load-state';
import { ListingBasicsModal, PromoteUnitInfo } from '../../dashboard/dashboard-stays-components/listing-basics-modal';
import { HouseRulesEditor } from '../checkin/house-rules-editor';
import { GuideEditorForm } from '../guide/guide-editor-form';
import { AccessDetailsForm } from '../guide/access-details-form';
import { GuidePreview } from '../guide/guide-preview';
import { CalendarSyncTab } from '../sync/calendar-sync-tab';
import { AddStayFlow } from '../add-stay-modal';
import { StayLinkPanel } from '../stay-link-panel';
import { headOf } from '../listing/stays-listing-routes';
import { formatStayDate } from '../../../onboarding/stay/stay-steps';
import { ReadinessChecklist } from './readiness-checklist';
import { nextStep, openingStep, openSetupAfterPromote, prevStep, setupSteps, stepDone, stepIndex } from './setup-steps';
import { useDeskInPlan } from '../../../hooks/use-desk-in-plan';
import { w } from '../../../../stays/wording';
import { AddPropertyForm } from '../properties/add-property-form';
import { propertyRights } from '../properties/properties-model';
import {
  Body,
  Choice,
  Choices,
  ChoiceText,
  Facts,
  Fill,
  Frame,
  Nav,
  NavGroup,
  NextLine,
  Panel,
  Progress,
  Quiet,
  Rail,
  RailButton,
  RailList,
  RailMark,
  StepPill,
  StepTitle,
  Track,
  Why,
} from './setup-wizard.styled';

/**
 * SET UP A LISTING — /dashboard/stays/setup and /dashboard/stays/setup/<listing id>?step=<key>
 * (Independent Stays plan ISR, ISR.7, R-ISR.6).
 *
 * The owner, 2026-10-27: "i get lost as a user i have no idea whats going on
 * it should be a nice wizard pipeline that will guide me through the steps of
 * creating my own stays". One step per screen, a rail of step names with
 * ticks (desktop) or a progress bar with "Step n of 7" (phone), each step
 * saying why it matters and what comes next, Back / Continue / Finish later.
 *
 * THE STEPS EMBED THE EXISTING EDITORS (S1 basics, S6 house rules, S7 guide +
 * access + preview, S5 calendar sync, S4 add-a-stay, S6 link panel) — never a
 * copy, so the wizard and the listing tabs can never drift. Progress is saved
 * on the listing (PUT …/setup) on every move; "Finish later" keeps the place.
 * The ticks come from the server's readiness answer, never from the page.
 */
export const SetupWizard: React.FC<{ listingId: string | null }> = ({ listingId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const canPromote = can(permissions, PERMISSIONS.STAYS_PROMOTE);
  // IS5 (IS5-T1): setting up an EXISTING listing is stays:listing_edit (a
  // co-host's own listing); starting from a unit or a new property is promote.
  const canSetUp = listingId ? can(permissions, PERMISSIONS.STAYS_LISTING_EDIT) : canPromote;
  // IS5.4: the Team tab's own gate (GET /stays/team) plus sending an invitation.
  const canInvite = can(permissions, PERMISSIONS.USER_READ) && can(permissions, PERMISSIONS.INVITE_CREATE);
  const canManage = can(permissions, PERMISSIONS.STAYS_MANAGE);
  const desk = useDeskInPlan();
  const SETUP_STEPS = setupSteps(desk);
  const asked = new URLSearchParams(location.search).get('step');

  const [view, setView] = useState<'loading' | 'error' | 'ready'>(listingId ? 'loading' : 'ready');
  const [listing, setListing] = useState<StaysListing | null>(null);
  const [readiness, setReadiness] = useState<ListingReadiness | null>(null);
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const [saving, setSaving] = useState(false);
  const seq = useRef(0);

  const load = useCallback(
    (quiet = false) => {
      if (!listingId) return;
      const mine = ++seq.current;
      if (!quiet) setView('loading');
      Promise.all([getStaysListing(listingId), getReadiness(listingId)])
        .then(([l, r]) => {
          if (mine !== seq.current) return;
          setListing(l);
          setReadiness(r);
          setView('ready');
        })
        .catch(() => {
          if (mine === seq.current && !quiet) setView('error');
        });
    },
    [listingId]
  );

  useEffect(() => {
    load();
  }, [load]);

  const items = readiness ? readiness.items : [];
  const completed = readiness ? readiness.setup.completed : [];
  const current: SetupStepKey = openingStep(asked, !!listingId, readiness ? readiness.setup.step : null, items);
  const index = stepIndex(current);
  const meta = SETUP_STEPS[index];
  const following = nextStep(current);

  const goTo = async (key: SetupStepKey, markDone: boolean) => {
    setDirection(stepIndex(key) >= index ? 'forward' : 'back');
    if (listingId) {
      setSaving(true);
      try {
        const out = await saveSetupProgress(listingId, { step: key, ...(markDone ? { completed: [current] } : {}) });
        setReadiness(r => (r ? { ...r, setup: out.setup } : r));
      } catch {
        /* the place is a convenience: moving on never waits for it */
      } finally {
        setSaving(false);
      }
      load(true);
      navigate(`/dashboard/stays/setup/${listingId}?step=${key}`);
    }
  };

  const finishLater = async () => {
    if (listingId) {
      try {
        await saveSetupProgress(listingId, { step: current });
      } catch {
        /* as above */
      }
      navigate(`/dashboard/stays/${listingId}`);
    } else navigate('/dashboard/stays');
  };

  if (!canSetUp) {
    return (
      <Panel>
        <StepTitle>Set up a listing</StepTitle>
        <Why>Setting up a listing is for owners, property managers and the listing&apos;s co-hosts.</Why>
        <Link to="/dashboard/stays">Back to Stays</Link>
      </Panel>
    );
  }

  const renderStep = () => {
    if ((!listingId || current === 'unit') && !desk) {
      // Independent Stays IS2: without Desk the host adds the property here.
      if (listing) return <UnitStep listing={listing} onPromoted={() => undefined} />;
      if (!propertyRights(permissions).canAdd) {
        return <Quiet>Only the account owner can add a property. Ask them to add it, then set up its listing here.</Quiet>;
      }

      return (
        <AddPropertyForm
          onCreated={r => {
            if (r.listing_ids[0]) void openSetupAfterPromote(navigate, r.listing_ids[0]);
          }}
        />
      );
    }
    if (!listingId || current === 'unit') {
      return (
        <UnitStep
          listing={listing}
          onPromoted={l => {
            void openSetupAfterPromote(navigate, l.id);
          }}
        />
      );
    }
    if (!listing) return null;
    switch (current) {
      case 'basics':
        return (
          <BasicsStep
            listing={listing}
            onChanged={l => {
              setListing(l);
              load(true);
            }}
          />
        );
      case 'rules':
        return <HouseRulesEditor listingId={listing.id} canManage={canManage || canPromote} />;
      case 'arrival':
        return <ArrivalStep listing={listing} canManage={canManage || canPromote} onChanged={() => load(true)} />;
      case 'calendars':
        return <CalendarSyncTab listing={headOf(listing)} canManage={canManage || canPromote} />;
      case 'first_stay':
        return <FirstStayStep listing={listing} onChanged={() => load(true)} />;
      case 'send_link':
      default:
        return <SendLinkStep listing={listing} readiness={readiness} onPreviewed={() => load(true)} />;
    }
  };

  const pct = Math.round(((index + 1) / SETUP_STEPS.length) * 100);

  return (
    <Frame data-setup-wizard="" data-step={current}>
      <Rail aria-label="Setup steps">
        <RailList>
          {SETUP_STEPS.map((s, i) => {
            const done = stepDone(s.key, items, completed, !!listingId);
            const reachable = !!listingId || s.key === 'unit';

            return (
              <li key={s.key}>
                <RailButton
                  type="button"
                  $current={s.key === current}
                  aria-current={s.key === current ? 'step' : undefined}
                  disabled={!reachable || saving}
                  onClick={() => goTo(s.key, false)}
                >
                  <RailMark $done={done} aria-hidden="true">
                    {done ? '✓' : i + 1}
                  </RailMark>
                  <span>
                    {s.title}
                    {done ? <VisuallyHidden> (done)</VisuallyHidden> : null}
                  </span>
                </RailButton>
              </li>
            );
          })}
        </RailList>
      </Rail>

      <div>
        <Progress>
          <StepPill>
            Step {index + 1} of {SETUP_STEPS.length}: {meta.title}
          </StepPill>
          <Track aria-hidden="true">
            <Fill $pct={pct} />
          </Track>
        </Progress>
        <Panel aria-labelledby="setup-step-title">
          <StateSwitch state={view}>
            {view === 'loading' ? (
              <FormSkeleton fields={4} label="the setup" />
            ) : view === 'error' ? (
              <LoadError message="Could not open this listing's setup." onRetry={() => load()} />
            ) : (
              <StepTransition stepKey={current} direction={direction}>
                <StepTitle id="setup-step-title">{meta.title}</StepTitle>
                <Why>{meta.why}</Why>
                <Body>{renderStep()}</Body>
                <Nav>
                  <NavGroup>
                    {prevStep(current) && listingId ? (
                      <AppButton variant="secondary" onClick={() => goTo(prevStep(current) as SetupStepKey, false)} disabled={saving}>
                        Back
                      </AppButton>
                    ) : null}
                    <AppButton variant="ghost" onClick={finishLater} disabled={saving}>
                      Finish later
                    </AppButton>
                  </NavGroup>
                  <NavGroup>
                    {following && listingId ? (
                      <>
                        <NextLine>Next: {SETUP_STEPS[stepIndex(following)].title}</NextLine>
                        <AppButton onClick={() => goTo(following, true)} loading={saving} loadingLabel="Saving…">
                          {meta.optional ? 'Continue (or skip)' : 'Continue'}
                        </AppButton>
                      </>
                    ) : null}
                    {/* IS5.4 (plan C-5): the guide ends by offering the team — a co-host
                        or a property manager to help with this listing. */}
                    {!following && listingId && canInvite ? (
                      <AppButton variant="secondary" onClick={() => navigate('/dashboard/stays/team')}>
                        Invite your team
                      </AppButton>
                    ) : null}
                    {!following && listingId ? (
                      <AppButton onClick={() => navigate(`/dashboard/stays/${listingId}`)}>Go to the listing</AppButton>
                    ) : null}
                  </NavGroup>
                </Nav>
              </StepTransition>
            )}
          </StateSwitch>
        </Panel>
      </div>
    </Frame>
  );
};

/* ── 1. Which unit ── */

const UnitStep: React.FC<{ listing: StaysListing | null; onPromoted: (l: StaysListing) => void }> = ({
  listing,
  onPromoted,
}) => {
  const desk = useDeskInPlan();
  const [buildings, setBuildings] = useState<StaysBuilding[] | null>(null);
  const [units, setUnits] = useState<Record<number, StaysBuildingUnits>>({});
  const [failed, setFailed] = useState(false);
  const [promote, setPromote] = useState<PromoteUnitInfo | null>(null);
  const [opening, setOpening] = useState<number | null>(null);

  useEffect(() => {
    if (listing) return;
    let live = true;
    listStaysBuildings()
      .then(async b => {
        if (!live) return;
        setBuildings(b.items);
        const on = b.items.filter(x => x.stays_enabled);
        const lists = await Promise.all(on.map(x => listBuildingStaysUnits(x.building_id).catch(() => null)));
        if (!live) return;
        const map: Record<number, StaysBuildingUnits> = {};
        lists.forEach(u => {
          if (u) map[u.building_id] = u;
        });
        setUnits(map);
      })
      .catch(() => live && setFailed(true));

    return () => {
      live = false;
    };
  }, [listing]);

  if (listing) {
    return (
      <Facts>
        <dt>Listing</dt>
        <dd>{listing.display_name}</dd>
        <dt>{w('Building', desk)}</dt>
        <dd>{listing.building_name}</dd>
        <dt>{w('Unit', desk)}</dt>
        <dd>{listing.unit_number}</dd>
      </Facts>
    );
  }
  if (failed) return <Quiet role="alert">Could not load your buildings. Reload the page to try again.</Quiet>;
  if (!buildings) return <FormSkeleton fields={3} label="your buildings" />;

  const choose = async (unitId: number) => {
    setOpening(unitId);
    try {
      const s = await getUnitStays(unitId);
      setPromote({
        unit_id: s.unit_id,
        unit_number: s.unit_number,
        unit_name: s.unit_name,
        building_id: s.building_id,
        building_name: s.building_name,
        building_timezone: s.building_timezone,
        building_country: s.building_country,
      });
    } finally {
      setOpening(null);
    }
  };

  return (
    <>
      {buildings.length === 0 ? <Quiet>You have no buildings yet. Add one in Buildings first.</Quiet> : null}
      {buildings.map(b => (
        <section key={b.building_id} aria-label={b.name}>
          <ChoiceText as="h3">{b.name}</ChoiceText>
          {!b.stays_enabled ? (
            <Quiet>
              Stays is off in this building. Switch it on in the building&apos;s settings, then come back.
            </Quiet>
          ) : (
            <Choices>
              {((units[b.building_id] && units[b.building_id].units) || []).map(u => (
                <Choice key={u.unit_id} data-unit={u.unit_number}>
                  <ChoiceText>
                    Unit {u.unit_number}
                    {u.unit_name ? ` · ${u.unit_name}` : ''}
                  </ChoiceText>
                  {u.listing_id ? (
                    <Link to={`/dashboard/stays/setup/${u.listing_id}`} aria-label={`Open the setup of unit ${u.unit_number} in ${b.name}`}>
                      Already listed: open its setup
                    </Link>
                  ) : u.eligible ? (
                    <AppButton
                      variant="secondary"
                      size="compact"
                      onClick={() => choose(u.unit_id)}
                      loading={opening === u.unit_id}
                      loadingLabel="Opening…"
                      aria-label={`Use unit ${u.unit_number} in ${b.name}`}
                    >
                      Use this unit
                    </AppButton>
                  ) : (
                    <Quiet>{u.reason || 'Not available for stays.'}</Quiet>
                  )}
                </Choice>
              ))}
            </Choices>
          )}
        </section>
      ))}
      <ListingBasicsModal
        open={!!promote}
        mode="promote"
        unit={promote}
        onClose={() => setPromote(null)}
        onSaved={l => {
          setPromote(null);
          onPromoted(l);
        }}
      />
    </>
  );
};

/* ── 2. Listing basics ── */

const BasicsStep: React.FC<{ listing: StaysListing; onChanged: (l: StaysListing) => void }> = ({ listing, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const opener = listing.can.activate ? 'activate' : listing.can.resume ? 'resume' : null;

  const open = async () => {
    if (!opener) return;
    setBusy(true);
    setProblem(null);
    try {
      onChanged(await actOnStaysListing(listing.id, opener, listing.version));
    } catch (e) {
      setProblem(e instanceof Error ? e.message : 'Could not open the listing for stays.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Facts>
        <dt>Name guests see</dt>
        <dd>{listing.display_name}</dd>
        <dt>Check-in from</dt>
        <dd>{listing.check_in_time}</dd>
        <dt>Check-out by</dt>
        <dd>{listing.check_out_time}</dd>
        <dt>Sleeps</dt>
        <dd>{listing.max_guests}</dd>
        <dt>Status</dt>
        <dd>
          <StatusPill tone={listing.status === 'active' ? 'success' : 'warning'} dot>
            {listing.status === 'active' ? 'Open for stays' : listing.status === 'paused' ? 'Paused' : 'Not open yet'}
          </StatusPill>
        </dd>
      </Facts>
      <div>
        <AppButton variant="secondary" onClick={() => setEditing(true)}>
          Edit basics
        </AppButton>{' '}
        {listing.status !== 'active' && opener ? (
          <AppButton onClick={open} loading={busy} loadingLabel="Opening…">
            Open for stays
          </AppButton>
        ) : null}
      </div>
      {problem ? <Quiet role="alert">{problem}</Quiet> : null}
      <ListingBasicsModal
        open={editing}
        mode="edit"
        listing={listing}
        onClose={() => setEditing(false)}
        onSaved={l => {
          setEditing(false);
          onChanged(l);
        }}
      />
    </>
  );
};

/* ── 4. Arrival: the guide and the door access ── */

const ArrivalStep: React.FC<{ listing: StaysListing; canManage: boolean; onChanged: () => void }> = ({
  listing,
  canManage,
  onChanged,
}) => {
  const [bundle, setBundle] = useState<GuideBundle | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    setFailed(false);
    getGuide(listing.id)
      .then(setBundle)
      .catch(() => setFailed(true));
  }, [listing.id]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) return <LoadError message="Could not load the arrival guide." onRetry={load} />;
  if (!bundle) return <FormSkeleton fields={5} label="the arrival guide" />;

  return (
    <>
      <GuideEditorForm
        listingId={listing.id}
        bundle={bundle}
        canManage={canManage}
        onSaved={guide => {
          setBundle(b => (b ? { ...b, guide } : b));
          onChanged();
        }}
        onReload={load}
      />
      <AccessDetailsForm
        listingId={listing.id}
        revealMinutes={bundle.guide.reveal_minutes}
        expiryMinutes={bundle.guide.expiry_minutes}
        onSaved={() => onChanged()}
      />
      <Quiet>
        Photos, questions and local tips can be added any time from the listing&apos;s Guide tab.
      </Quiet>
    </>
  );
};

/* ── 6. Your first stay ── */

const FirstStayStep: React.FC<{ listing: StaysListing; onChanged: () => void }> = ({ listing, onChanged }) => {
  // The listing the sheet was OPENED with, held still: saving reloads the wizard's listing (a new object), and
  // handing that to AddStayFlow reset it and closed the show-once link panel (journey:isr run 6).
  const [adding, setAdding] = useState<StaysListing | null>(null);
  // The stays already added, so a saved stay is seen here (the user's own try, 2026-10-27).
  const [stays, setStays] = useState<StayReservation[]>([]);

  const loadStays = useCallback(() => {
    listListingReservations(listing.id, { state: 'upcoming' })
      .then(r => setStays(r.items))
      .catch(() => setStays([]));
  }, [listing.id]);

  useEffect(() => {
    loadStays();
  }, [loadStays]);

  return (
    <>
      <Quiet>
        Stays from your booking-site calendars arrive by themselves. To add one by hand, pick the dates and the guest who is
        coming; the check-in link is made with it.
      </Quiet>
      {stays.length ? (
        <Choices aria-label="Stays added">
          {stays.map(s => (
            <Choice key={s.id}>
              <ChoiceText>
                Added: {s.lead.first_name} {s.lead.last_name} · {formatStayDate(s.check_in)} to {formatStayDate(s.check_out)}
              </ChoiceText>
            </Choice>
          ))}
        </Choices>
      ) : null}
      <div>
        <AppButton variant={stays.length ? 'secondary' : undefined} onClick={() => setAdding(listing)}>
          {stays.length ? 'Add another stay' : 'Add a stay'}
        </AppButton>
      </div>
      <AddStayFlow
        listing={adding}
        onClose={() => setAdding(null)}
        onSaved={() => {
          loadStays();
          onChanged();
        }}
      />
    </>
  );
};

/* ── 7. Preview, then send the link ── */

const SendLinkStep: React.FC<{
  listing: StaysListing;
  readiness: ListingReadiness | null;
  onPreviewed: () => void;
}> = ({ listing, readiness, onPreviewed }) => {
  const [previewing, setPreviewing] = useState(false);
  const [stays, setStays] = useState<StayReservation[] | null>(null);
  const [open, setOpen] = useState<StayReservation | null>(null);

  const loadStays = useCallback(() => {
    listListingReservations(listing.id, { state: 'upcoming' })
      .then(r => setStays(r.items))
      .catch(() => setStays([]));
  }, [listing.id]);

  useEffect(() => {
    loadStays();
  }, [loadStays]);

  const preview = async () => {
    setPreviewing(true);
    try {
      await saveSetupProgress(listing.id, { step: 'send_link', previewed: true });
    } catch {
      /* the preview still opens */
    }
    onPreviewed();
  };

  return (
    <>
      {previewing ? (
        <GuidePreview listingId={listing.id} />
      ) : (
        <div>
          <AppButton variant="secondary" onClick={preview}>
            Preview the guest page
          </AppButton>
        </div>
      )}
      {stays === null ? (
        <FormSkeleton fields={2} label="your stays" />
      ) : stays.length === 0 ? (
        <Quiet>No upcoming stay yet. Add one on the step before, or wait for your booking-site calendar.</Quiet>
      ) : (
        <Choices>
          {stays.map(s => (
            <Choice key={s.id}>
              <ChoiceText>
                {s.lead.first_name} {s.lead.last_name} · {formatStayDate(s.check_in)} to {formatStayDate(s.check_out)}
              </ChoiceText>
              <AppButton
                variant="secondary"
                size="compact"
                onClick={() => setOpen(s)}
                aria-label={`Send the check-in link to ${s.lead.first_name} ${s.lead.last_name}`}
              >
                {s.checkin.sent_at ? 'Send again' : 'Send the link'}
              </AppButton>
            </Choice>
          ))}
        </Choices>
      )}
      <StayLinkPanel
        open={!!open}
        reservation={open}
        onClose={() => setOpen(null)}
        onChanged={loadStays}
      />
      <ReadinessChecklist listingId={listing.id} canFix readiness={readiness} />
    </>
  );
};
